/**
 * React bindings for the store.
 *
 * useSyncExternalStore subscribes a component to one slice and re-renders it
 * only when that slice's reference changes, which the store guarantees by
 * replacing a slice object on every mutation. The getSnapshot returns the live
 * slice, stable by reference until it changes, so there is no render loop.
 */

import { useSyncExternalStore } from 'react';
import { subscribe, getSlice } from './store';
import type { AppState, SliceName } from '../lib/types';

/** Subscribe the calling component to one slice of state. */
export function useSlice<K extends SliceName>(name: K): AppState[K] {
  return useSyncExternalStore(
    (onChange) => subscribe(name, onChange),
    () => getSlice(name),
    () => getSlice(name),
  );
}

/** Whether a usable session is present, derived from the account slice. */
export function useAuthed(): boolean {
  return useSlice('accounts').current.authenticated;
}
